import { Component, Input } from '@angular/core';
import {
  lucidCircularTickIcon,
  lucidWarningIcon,
} from '@g99-product-web-ui/shared-util-lib';

import { MultiFileUploadService } from '../multi-file-upload.service';

@Component({
  selector: 'lib-file-upload-size-warning',
  templateUrl: './file-upload-size-warning.component.html',
  styleUrl: './file-upload-size-warning.component.scss',
  standalone: false,
})
export class FileUploadSizeWarningComponent {
  @Input() public maxTotalSizeMB?: number;
  @Input() public totalSize = 0;
  @Input() public fileCount = 0;

  protected warningIcon = lucidWarningIcon;
  protected tickIcon = lucidCircularTickIcon;
  constructor(
    private readonly _multiFileUploadService: MultiFileUploadService
  ) {}
  // ---------------------- Validation Flags -----------------------

  /**
   * @purpose Determines whether a warning should be displayed
   * when the uploaded file(s) exceed the maximum allowed total size.
   */
  protected get showWarning(): boolean {
    if (!this.maxTotalSizeMB || this.totalSize === 0) {
      return false;
    }
    return this.totalSize > this.maxTotalSizeMB * 1024 * 1024;
  }

  /**
   * @purpose Determines whether a success message should be displayed
   * when the uploaded file(s) are within the maximum allowed total size.
   */
  protected get showSuccess(): boolean {
    if (!this.maxTotalSizeMB || this.totalSize === 0) {
      return false;
    }
    return this.totalSize <= this.maxTotalSizeMB * 1024 * 1024;
  }

  // ---------------------- Progress & Limits -----------------------

  /**
   * @purpose Calculates the progress percentage of the uploaded file(s)
   * against the maximum allowed size.
   */
  protected get progressPercentage(): number {
    if (!this.maxTotalSizeMB) {
      return 0;
    }
    const maxSizeBytes = this.maxTotalSizeMB * 1024 * 1024;
    return Math.min(100, Math.round((this.totalSize / maxSizeBytes) * 100));
  }

  /**
   * @purpose Calculates the excess size in bytes that exceeds
   * the maximum allowed size.
   */
  protected get excessSize(): number {
    if (!this.maxTotalSizeMB) {
      return 0;
    }
    const maxSizeBytes = this.maxTotalSizeMB * 1024 * 1024;
    return Math.max(0, this.totalSize - maxSizeBytes);
  }

  // ---------------------- Messages -----------------------

  /**
   * @purpose Returns a warning message when uploaded file(s)
   * exceed the allowed size.
   */
  protected get warningMessage(): string {
    const excess = this.excessSize;
    if (excess === 0) {
      return '';
    }

    if (this.fileCount === 1) {
      return 'This file exceeds the maximum size limit. Please select a smaller file.';
    } else {
      return `These ${this.fileCount} files exceed the total size limit by ${this.formatFileSize(
        excess
      )}. Please remove some files or select smaller ones.`;
    }
  }

  /**
   * @purpose Returns a success message when uploaded file(s)
   * are within the allowed size limit.
   */
  protected get successMessage(): string {
    if (this.totalSize === 0) {
      return '';
    }

    if (this.fileCount === 1) {
      return 'File is ready for upload.';
    } else {
      return `All ${this.fileCount} files are within the size limit and ready for upload.`;
    }
  }

  // ---------------------- Utilities -----------------------

  /**
   * @purpose Converts a file size in bytes into a human-readable format
   * (e.g., KB, MB, GB).
   */
  protected formatFileSize(bytes: number | undefined): string {
    return this._multiFileUploadService.formatFileSize(bytes ?? 0);
  }
}
